// ...existing imports...
import React, { useEffect, useState, useRef } from 'react';
import { BellOutlined } from '@ant-design/icons';
import NotificationDrawer from './notification-drawer';
import { Badge } from 'antd';
import notificationService from 'services/notification';
import useDidUpdate from 'helpers/useDidUpdate';

const initialPerPage = 15;

export default function NotificationBar() {
  // Initialize muted from localStorage
  const [muted, setMuted] = useState(() => {
    const stored = localStorage.getItem('notificationMuted');
    return stored === 'true';
  });
  const [notificationDrawer, setNotificationDrawer] = useState(false);
  const [notificationList, setNotificationList] = useState([]);
  const [lastSeenNotification, setLastSeenNotification] = useState(0);
  const [newNotification, setNewNotification] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isRinging, setIsRinging] = useState(false);
  const [paginate, setPaginate] = useState({
    page: 1,
    perPage: initialPerPage,
  });

  const audioRef = useRef(null);

  useEffect(() => {
    audioRef.current = new Audio('/notification.mp3');
    audioRef.current.loop = true;
    audioRef.current.volume = muted ? 0 : 0.2; // Set volume based on muted

    const fetchNotificationStatistic = () => {
      notificationService.getStatistics().then((res) => {
        const unreadCount = getUnreadNotificationsCount(res);
        setNewNotification(unreadCount);

        // Only play sound if there are new notifications since last seen, and drawer is closed
        if (unreadCount > lastSeenNotification && !notificationDrawer) {
          if (!isRinging) {
            setIsRinging(true);
            playSound();
          }
        } else {
          stopSound();
          setIsRinging(false);
        }
      });
    };

    fetchNotificationStatistic();

    const intervalId = setInterval(fetchNotificationStatistic, 60000);

    return () => {
      clearInterval(intervalId);
      stopSound();
    };
    // eslint-disable-next-line
  }, [muted, lastSeenNotification, notificationDrawer]); // Only run once on mount

  // Save muted state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('notificationMuted', muted);
    if (audioRef.current) {
      audioRef.current.volume = muted ? 0 : 0.2;
    }
  }, [muted]);

  useDidUpdate(() => {
    if (paginate?.page > 1) {
      fetchNotificationList(paginate);
    }
  }, [paginate?.page]);

  const fetchNotificationList = (paginate) => {
    setLoading(true);
    notificationService
      .getAll({
        type: 'notification',
        column: 'id',
        sort: 'desc',
        perPage: initialPerPage,
        page: 1,
        ...paginate,
      })
      .then((res) => {
        setNotificationList((prev) => [...prev, ...res?.data]);
      })
      .finally(() => setLoading(false));
  };

  const fetchReadAllNotifications = () => {
    return notificationService.readAll().then(() => {
      stopSound();
      setNewNotification(0);
      setIsRinging(false);
    });
  };

  const handleOpenNotificationDrawer = (event) => {
    event.stopPropagation();
    fetchNotificationList(paginate);
    setNotificationDrawer(true);
    setLastSeenNotification(newNotification);
    stopSound();
    setIsRinging(false);
  };

  const handleReadAllNotifications = () => {
    setLoading(true);
    return fetchReadAllNotifications().finally(() => setLoading(false));
  };

  const handleClose = () => {
    setNotificationDrawer(false);
    setPaginate({ page: 1, perPage: initialPerPage });
    setNotificationList([]);
  };

  const getUnreadNotificationsCount = (notificationData) => {
    return notificationData?.notification || 0;
  };

  const playSound = () => {
    if (audioRef.current && audioRef.current.paused) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch((err) => {
        console.warn('Audio play failed:', err);
      });
    }
  };

  const stopSound = () => {
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  // Toggle mute state: just set volume to 0 or 0.2, do not stop sound
  const toggleMute = () => {
    setMuted((prev) => !prev);
  };

  return (
    <>
      <style>
        {`
          .icon-button {
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            justify-content: center;
          }

          .ringing-bell {
            color: blue;
            animation: ring-animation 0.5s infinite alternate;
          }

          @keyframes ring-animation {
            0% { transform: rotate(0deg); }
            25% { transform: rotate(-10deg); }
            50% { transform: rotate(10deg); }
            75% { transform: rotate(-5deg); }
            100% { transform: rotate(5deg); }
          }
        `}
      </style>

      <span className='icon-button' onClick={handleOpenNotificationDrawer}>
        <Badge count={newNotification}>
          <BellOutlined
            className={isRinging ? 'ringing-bell' : ''}
            style={{ fontSize: 24 }}
          />
        </Badge>
      </span>

      <NotificationDrawer
        visible={notificationDrawer}
        handleClose={handleClose}
        list={notificationList}
        readAll={handleReadAllNotifications}
        loading={loading}
        paginate={paginate}
        setPaginate={setPaginate}
        muted={muted}
        toggleMute={toggleMute}
      />
    </>
  );
}
